/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo, useRef } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { MenuGroup } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useUCMenuItems } from '@atlassian/navigation-bff-data-provider/universal-create';

import { MAX_ALLOWED_SECTIONS, MAX_MENU_HEIGHT } from '../../../constants';
import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks/use-universal-create-analytics';
import { GenericErrorBoundary, withIntlProvider } from '../../../services';
import type { RenderableMenuSectionData } from '../../../types/menuV2';
import { LoadingMenu } from '../LoadingMenu';
import { isNativeRenderableMenuItem } from '../MenuItemV2';
import { getFoldableThreshold, hasMoreButton } from '../MenuSectionPrimitive/utils';
import { RenderableMenuSection } from '../RenderableMenuSection';

import { sectionsToAppMenuConfig } from './utils/configConverters';
import { computeRenderableSections, computeSectionMetadata } from './utils/sectionComputations';

const styles = cssMap({
	menuWrap: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

function MenuContentV2Inner() {
	const { locale } = useIntl();
	const { sections, crossAppCreationConfig, product, subProduct } = useMenuControl();
	const {
		cloudId = '',
		orgId = '',
		accountId = '',
	} = crossAppCreationConfig?.enabled ? crossAppCreationConfig : {};

	// Determine if we're in native-only mode (no cross-app creation)
	const isNativeOnly = !crossAppCreationConfig?.enabled;

	// Skip appMenuConfig generation when in native-only mode
	const appMenuConfig = useMemo(
		() => sectionsToAppMenuConfig(isNativeOnly ? [] : sections),
		[isNativeOnly, sections],
	);

	const { data, isLoading, hasError, fetchUCMenuItems } = useUCMenuItems({
		cloudId,
		orgId,
		product,
		subProduct,
		userId: accountId,
		locale,
		appMenuConfig,
		skipInitialization: isNativeOnly,
	});

	// Fetch menu items when cross-app creation is enabled
	useEffect(() => {
		if (crossAppCreationConfig?.enabled) {
			fetchUCMenuItems();
		}
	}, [crossAppCreationConfig?.enabled, fetchUCMenuItems]);

	const renderableSections = useMemo(
		() =>
			computeRenderableSections({
				sections,
				data,
				hasError,
				isNativeOnly,
			}),
		[sections, data, hasError, isNativeOnly],
	);

	const sectionsToAnalyze = useMemo(
		() => renderableSections.slice(0, MAX_ALLOWED_SECTIONS),
		[renderableSections],
	);

	// Pre-compute section metadata (start indices and total count) to avoid O(n²) complexity
	const sectionMetadata = useMemo(
		() => computeSectionMetadata(sectionsToAnalyze),
		[sectionsToAnalyze],
	);

	// Only show loading state when cross-app creation is enabled
	// This ensures native-only items render immediately when cross-app is disabled
	if (isLoading && crossAppCreationConfig?.enabled) {
		return <LoadingMenu padded />;
	}

	if (sectionsToAnalyze.length === 0) {
		return null;
	}

	return (
		<MenuGroup
			spacing="compact"
			testId="global-create-content-popover-menu-v2"
			{...(sectionsToAnalyze.some((section) => typeof section.foldable === 'number') &&
				{ maxHeight: MAX_MENU_HEIGHT })}
		>
			<Box xcss={cx(styles.menuWrap)}>
				<AnalyticsTracker sections={sectionsToAnalyze} />
				{sectionsToAnalyze.map((section, sectionIndex) => (
					<RenderableMenuSection
						section={section}
						sectionIndex={sectionIndex}
						totalMenuItemsCount={sectionMetadata.totalCount}
						sectionStartIndex={sectionMetadata.startIndices[sectionIndex]}
						key={`menu-section-${section.type}-${sectionIndex}`}
					/>
				))}
			</Box>
		</MenuGroup>
	);
}

export function AnalyticsTracker({ sections }: { sections: RenderableMenuSectionData[] }) {
	const { fireNewMenuItemsViewed } = useUniversalCreateAnalytics();
	const analyticsFired = useRef(false);

	useEffect(() => {
		if (sections.length === 0 || analyticsFired.current) {
			return;
		}

		analyticsFired.current = true;
		const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');

		// Flatten all items from all sections
		const allItems = sections.flatMap((section) => section.items);

		// Build metadata for each section
		const sectionMetadata = sections.map((section, sectionIndex) => {
			const sectionItems = section.items;

			// Extract all ATIs and types from items in this section
			const sectionItemAtis = sectionItems.map((item) =>
				isNativeRenderableMenuItem(item) ? item.ati.toString() : item.ati || 'unknown',
			);

			const sectionItemTypes = sectionItems.map((item) =>
				isNativeRenderableMenuItem(item) ? 'native' : item.type,
			);

			const moreButtonEnabled = hasMoreButton(section.foldable, sectionItems.length);

			const inMoreItemAtisList = moreButtonEnabled
				? sectionItems
						.slice(getFoldableThreshold(section.foldable))
						.map((item) => item.ati.toString())
				: undefined;
			const inMoreItemCount = moreButtonEnabled
				? sectionItems.length - getFoldableThreshold(section.foldable)
				: undefined;

			// Create metadata object for this section
			const baseMetadata = {
				groupHeading: section.heading,
				groupIndex: sectionIndex,
				groupItemCount: sectionItems.length,
				groupItemAtisList: sectionItemAtis,
				itemTypes: sectionItemTypes,
				showMoreButton: moreButtonEnabled,
				inMoreItemAtisList,
				inMoreItemCount,
			};

			// Add type-specific properties
			if (section.type === 'appDefinedSection') {
				return {
					...baseMetadata,
					type: 'appDefinedSection' as const,
					personalize: section.personalized ?? 'off',
				};
			} else {
				return {
					...baseMetadata,
					type: 'suggestionSection' as const,
				};
			}
		});

		// Fire analytics event with aggregated data
		fireNewMenuItemsViewed({
			crossAppCreationEnabled,
			groupCount: sections.length,
			groupList: sectionMetadata,
			totalCrossAppItemCount: allItems.filter((item) => !isNativeRenderableMenuItem(item)).length,
			totalNativeItemCount: allItems.filter(isNativeRenderableMenuItem).length,
		});
	}, [sections, fireNewMenuItemsViewed]);

	return null;
}

const MenuContentV2WithIntl = withIntlProvider(MenuContentV2Inner);

export const MenuContentV2 = () => (
	<GenericErrorBoundary name="menu-content-v2">
		<MenuContentV2WithIntl />
	</GenericErrorBoundary>
);
